/**
 * Deeper spells in the world (docs/spells-depth.md): the woven fields on the
 * floor (Stasis Field, Raincloud, Frozen Moment, Hourglass) and Time Stop's
 * screen: the world washed to blue-grey, a clock ring round the caster and
 * the seconds left. Purely visual: nothing here feeds the simulation.
 */

import type { Element } from '@shared/index.js';
import { isWoven } from '@shared/index.js';
import type { ViewHazard, ViewModel } from '../state/viewModel.js';
import { UI, elementColor, elementDark, elementSecondary, rgba } from '../palette.js';
import { TAU, UNIT, font, type Ctx } from './gfx.js';
import type { Camera } from './camera.js';

/** The woven piece a field was cast from (its dominant), for colour and look. */
function fieldPiece(els: readonly Element[]): Element {
  return els.find((e) => isWoven(e)) ?? 'arcane';
}

/** A field on the floor: a tinted disc with its piece's mark, fading over its last second. */
export function drawField(ctx: Ctx, cam: Camera, h: ViewHazard, time: number): void {
  // slice 2's fields draw as themselves
  if (h.sg && drawSliceTwoField(ctx, cam, h, time)) return;
  const px = cam.sx(h.rx), py = cam.sy(h.ry);
  const r = h.r * UNIT;
  const el = fieldPiece(h.el);
  const col = elementColor(el);
  const fade = Math.min(1, h.t);
  ctx.save();
  ctx.globalAlpha = 0.85 * fade;
  // the zone
  const g = ctx.createRadialGradient(px, py, r * 0.1, px, py, r);
  g.addColorStop(0, rgba(col, el === 'cloud' ? 0.12 : 0.22));
  g.addColorStop(1, rgba(col, 0.05));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.ellipse(px, py, r, r * 0.5, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(col, 0.7);
  ctx.lineWidth = 2;
  ctx.setLineDash(el === 'stillness' ? [6, 6] : []);
  ctx.beginPath();
  ctx.ellipse(px, py, r, r * 0.5, 0, 0, TAU);
  ctx.stroke();
  ctx.setLineDash([]);
  switch (el) {
    case 'time':
    case 'moment': {
      // a clock face on the floor; Time's hands crawl, a Moment's hold still
      const hands = el === 'time' ? time * 0.6 : 0.35;
      ctx.strokeStyle = rgba(col, 0.9);
      ctx.lineWidth = 3;
      for (let k = 0; k < 12; k++) {
        const a = (k / 12) * TAU;
        ctx.beginPath();
        ctx.moveTo(px + Math.cos(a) * r * 0.82, py + Math.sin(a) * r * 0.41);
        ctx.lineTo(px + Math.cos(a) * r * 0.92, py + Math.sin(a) * r * 0.46);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px + Math.cos(hands) * r * 0.6, py + Math.sin(hands) * r * 0.3);
      ctx.moveTo(px, py);
      ctx.lineTo(px + Math.cos(hands * 12) * r * 0.4, py + Math.sin(hands * 12) * r * 0.2);
      ctx.stroke();
      break;
    }
    case 'cloud': {
      // the cloud's shadow and its falling drops
      ctx.fillStyle = rgba('#3A4652', 0.25);
      ctx.beginPath();
      ctx.ellipse(px, py, r * 0.9, r * 0.42, 0, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = rgba('#BFE3FF', 0.7);
      ctx.lineWidth = 1.5;
      for (let k = 0; k < 18; k++) {
        const a = (k * 2.39996) % TAU;
        const d = ((k * 0.618) % 1) * r * 0.85;
        const fall = ((time * 1.7 + k * 0.37) % 1);
        const x = px + Math.cos(a) * d;
        const y = py + Math.sin(a) * d * 0.5 - (1 - fall) * 60;
        ctx.globalAlpha = 0.85 * fade * fall;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x - 2, y + 8);
        ctx.stroke();
      }
      break;
    }
    default: {
      // stasis: slow rings drifting inward
      for (let k = 0; k < 3; k++) {
        const f = 1 - ((time * 0.25 + k / 3) % 1);
        ctx.strokeStyle = rgba(col, 0.5 * f);
        ctx.beginPath();
        ctx.ellipse(px, py, r * f, r * 0.5 * f, 0, 0, TAU);
        ctx.stroke();
      }
    }
  }
  ctx.restore();
}

/** An ellipse on the floor (the arena's 2:1 squash). */
function floorRing(ctx: Ctx, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.ellipse(x, y, r, r * 0.5, 0, 0, TAU);
}

/** The rolling fields, the Whirlpool, the floods, the blizzard, the blaze and the Supernova's star. False: not one of these. */
function drawSliceTwoField(ctx: Ctx, cam: Camera, h: ViewHazard, time: number): boolean {
  const px = cam.sx(h.rx), py = cam.sy(h.ry);
  const r = h.r * UNIT;
  const fade = Math.min(1, h.t * 2);
  ctx.save();
  ctx.globalAlpha = fade;
  switch (h.sg) {
    case 'wave': {
      // a crest of water rolling along its aim, foam on top
      const ca = Math.cos(h.ang), sa = Math.sin(h.ang);
      ctx.fillStyle = rgba(elementColor('tide'), 0.55);
      floorRing(ctx, px, py, r);
      ctx.fill();
      // the crest: a bowed line across the aim at the wave's front, bobbing
      for (const [col, lift, width] of [[elementColor('tide'), 10, 9], ['#FFFFFF', 16, 4]] as const) {
        ctx.strokeStyle = rgba(col, 0.9);
        ctx.lineWidth = width;
        ctx.beginPath();
        for (let k = -6; k <= 6; k++) {
          const side = (k / 6) * r;
          const fwd = r * 0.6 * (1 - (k / 6) * (k / 6) * 0.5);
          const bob = Math.sin(time * 9 + k) * 2;
          const x = px + ca * fwd - sa * side;
          const y = py + (sa * fwd + ca * side) * 0.5 - lift + bob;
          if (k === -6) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      break;
    }
    case 'orb': {
      // a slow dark orb with a violet rim, swallowing light
      const lift = 26;
      const g = ctx.createRadialGradient(px, py - lift, r * 0.1, px, py - lift, r);
      g.addColorStop(0, rgba('#000000', 0.95));
      g.addColorStop(0.7, rgba(elementColor('void'), 0.9));
      g.addColorStop(1, rgba(elementSecondary('void'), 0));
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(px, py - lift, r, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = rgba(elementSecondary('void'), 0.8);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(px, py - lift, r * 0.8, time * 3, time * 3 + Math.PI * 1.3);
      ctx.stroke();
      ctx.fillStyle = rgba('#000000', 0.35);
      floorRing(ctx, px, py, r * 0.7);
      ctx.fill();
      break;
    }
    case 'roll': {
      // a great ice boulder with facets, turning as it rolls
      const lift = r * 0.6;
      ctx.fillStyle = rgba('#000000', 0.3);
      floorRing(ctx, px, py, r);
      ctx.fill();
      ctx.fillStyle = elementColor('glacier');
      ctx.strokeStyle = elementDark('glacier');
      ctx.lineWidth = 3;
      ctx.beginPath();
      const spin = time * 5;
      for (let k = 0; k < 7; k++) {
        const a = spin + (k / 7) * TAU;
        const rr = r * (0.82 + 0.12 * Math.sin(k * 2.3));
        const x = px + Math.cos(a) * rr, y = py - lift + Math.sin(a) * rr * 0.8;
        if (k === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.strokeStyle = rgba('#FFFFFF', 0.7);
      ctx.beginPath();
      ctx.moveTo(px - r * 0.3, py - lift - r * 0.4);
      ctx.lineTo(px + r * 0.1, py - lift - r * 0.1);
      ctx.stroke();
      break;
    }
    case 'pull': {
      // a whirlpool: spiral arms turning inward
      ctx.fillStyle = rgba(elementColor('whirlpool'), 0.3);
      floorRing(ctx, px, py, r);
      ctx.fill();
      ctx.strokeStyle = rgba(elementSecondary('whirlpool'), 0.8);
      ctx.lineWidth = 2.5;
      for (let arm = 0; arm < 3; arm++) {
        ctx.beginPath();
        for (let k = 0; k <= 24; k++) {
          const f = k / 24;
          const a = -time * 3 + arm * (TAU / 3) + f * 5;
          const rr = r * (1 - f * 0.9);
          const x = px + Math.cos(a) * rr, y = py + Math.sin(a) * rr * 0.5;
          if (k === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      break;
    }
    case 'flood': {
      // a sheet of water with rings spreading across it
      ctx.fillStyle = rgba(elementColor('deluge'), 0.35);
      floorRing(ctx, px, py, r);
      ctx.fill();
      ctx.strokeStyle = rgba(elementSecondary('deluge'), 0.5);
      ctx.lineWidth = 1.5;
      for (let k = 0; k < 5; k++) {
        const a = k * 2.4;
        const d = ((k * 0.37) % 1) * r * 0.7;
        const f = (time * 0.6 + k * 0.2) % 1;
        floorRing(ctx, px + Math.cos(a) * d, py + Math.sin(a) * d * 0.5, r * 0.25 * f);
        ctx.globalAlpha = fade * (1 - f);
        ctx.stroke();
      }
      break;
    }
    case 'winter': {
      // a blizzard: a frosted disc and snow blowing across it
      ctx.fillStyle = rgba(elementColor('winter'), 0.22);
      floorRing(ctx, px, py, r);
      ctx.fill();
      ctx.strokeStyle = rgba('#FFFFFF', 0.6);
      ctx.lineWidth = 2;
      floorRing(ctx, px, py, r);
      ctx.stroke();
      ctx.fillStyle = rgba('#FFFFFF', 0.9);
      for (let k = 0; k < 40; k++) {
        const a = (k * 2.39996) % TAU;
        const d = ((k * 0.618) % 1) * r;
        const drift = ((time * 0.8 + k * 0.13) % 1) * 40;
        ctx.fillRect(px + Math.cos(a) * d + drift - 20, py + Math.sin(a) * d * 0.5 - 30 + drift, 2, 2);
      }
      break;
    }
    case 'blaze': {
      ctx.fillStyle = rgba(elementColor('inferno'), 0.3);
      floorRing(ctx, px, py, r);
      ctx.fill();
      ctx.strokeStyle = rgba(elementSecondary('inferno'), 0.8);
      ctx.lineWidth = 3;
      ctx.beginPath();
      for (let k = 0; k <= 32; k++) {
        const a = (k / 32) * TAU;
        const flick = 1 + 0.12 * Math.sin(time * 14 + k * 1.7);
        const x = px + Math.cos(a) * r * flick, y = py + Math.sin(a) * r * 0.5 * flick;
        if (k === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      break;
    }
    case 'star': {
      // Supernova: a star that swells over its 2 s while the ring of its reach warns everyone off
      const life = Math.max(0, Math.min(1, 1 - h.t / 2));
      ctx.strokeStyle = rgba(UI.hpRed, 0.4 + 0.4 * Math.abs(Math.sin(time * 8)));
      ctx.lineWidth = 3;
      ctx.setLineDash([10, 8]);
      floorRing(ctx, px, py, r);
      ctx.stroke();
      ctx.setLineDash([]);
      const sr = UNIT * (0.4 + 1.6 * life);
      const lift = 50;
      const g = ctx.createRadialGradient(px, py - lift, 0, px, py - lift, sr * 2);
      g.addColorStop(0, rgba('#FFFFFF', 1));
      g.addColorStop(0.35, rgba(elementColor('sun'), 0.95));
      g.addColorStop(0.7, rgba(elementColor('plasma'), 0.5));
      g.addColorStop(1, rgba(elementColor('plasma'), 0));
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(px, py - lift, sr * 2, 0, TAU);
      ctx.fill();
      break;
    }
    default:
      ctx.restore();
      return false;
  }
  ctx.restore();
  return true;
}

/**
 * Over bodies in the world: a Death mark's countdown, a ward's ring, and under
 * the Lesser Time Stop a little stopped clock on every slowed foe.
 */
export function drawDepthMarks(ctx: Ctx, cam: Camera, vm: ViewModel, time: number): void {
  const lesser = (vm.wave?.ts ?? 0) > 0 && vm.wave?.tsl === 1;
  const caster = lesser ? vm.players.find((p) => p.id === vm.wave?.tsb) : undefined;
  const bodies: { x: number; y: number; dm?: number; wd?: 1; stopped: boolean }[] = [];
  for (const p of vm.players) {
    if (p.dead) continue;
    const foe = lesser && caster !== undefined && p.id !== caster.id && p.tm !== undefined && p.tm !== caster.tm;
    bodies.push({ x: p.rx, y: p.ry, ...(p.dm !== undefined ? { dm: p.dm } : {}), ...(p.wd ? { wd: p.wd } : {}), stopped: foe });
  }
  for (const e of vm.enemies) bodies.push({ x: e.rx, y: e.ry, ...(e.dm !== undefined ? { dm: e.dm } : {}), stopped: lesser && e.ob === undefined });
  ctx.save();
  for (const b of bodies) {
    const x = cam.sx(b.x), y = cam.sy(b.y);
    if (b.wd) {
      // a heartbeat ring: the next killing blow won't land
      const beat = 1 + 0.08 * Math.max(0, Math.sin(time * 6));
      ctx.strokeStyle = rgba(elementColor('pulse'), 0.8);
      ctx.lineWidth = 2;
      floorRing(ctx, x, y, UNIT * 0.75 * beat);
      ctx.stroke();
    }
    if (b.stopped) {
      ctx.strokeStyle = rgba(elementColor('moment'), 0.85);
      ctx.lineWidth = 2;
      floorRing(ctx, x, y, UNIT * 0.65);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + UNIT * 0.4, y - UNIT * 0.05);
      ctx.stroke();
    }
    if (b.dm !== undefined) {
      // a dark sigil overhead with the seconds left
      const top = y - UNIT * 2.1;
      const pulse = 0.6 + 0.4 * Math.abs(Math.sin(time * (b.dm < 1.5 ? 10 : 4)));
      ctx.fillStyle = rgba(elementDark('death'), 0.85);
      ctx.beginPath();
      ctx.arc(x, top, 13, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = rgba(elementSecondary('death'), pulse);
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.font = font(13, 900);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = rgba('#FFFFFF', 0.95);
      ctx.fillText(Math.ceil(b.dm).toString(), x, top + 1);
    }
  }
  ctx.restore();
}

/** The room-wide Grand Magicks' lines at the top, under Time Stop's: what runs and for how long. */
function drawGrandLines(ctx: Ctx, vm: ViewModel, w: number, h: number, startY: number): void {
  const lines: { text: string; color: string }[] = [];
  const wave = vm.wave;
  if (!wave) return;
  if ((wave.qk ?? 0) > 0) lines.push({ text: `QUICKENING  ${(wave.qk ?? 0).toFixed(1)}`, color: elementColor('flux') });
  if ((wave.fl ?? 0) > 0) lines.push({ text: `THE GREAT FLOOD  ${(wave.fl ?? 0).toFixed(1)}`, color: elementSecondary('deluge') });
  if ((wave.ew ?? 0) > 0) lines.push({ text: `ETERNAL WINTER  ${(wave.ew ?? 0).toFixed(1)}`, color: elementColor('winter') });
  if (lines.length === 0) return;
  // the Great Flood lies on the whole room
  if ((wave.fl ?? 0) > 0) {
    ctx.save();
    ctx.globalAlpha = 0.14 * Math.min(1, (wave.fl ?? 0) / 0.5);
    ctx.fillStyle = elementColor('deluge');
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }
  ctx.save();
  ctx.font = font(16, 900);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  lines.forEach((l, i) => {
    ctx.fillStyle = rgba(l.color, 0.95);
    ctx.fillText(l.text, w / 2, startY + i * 22);
  });
  ctx.restore();
}

/**
 * Time Stop on screen: everything washes to a cold grey-blue, the caster gets a
 * ticking clock ring, and the seconds left sit at the top. `ts` seconds left,
 * `fadeIn` seconds since it began (for the wash's swell). The Lesser Time Stop
 * only frosts the screen's edges (its slowed foes carry their own clocks,
 * drawDepthMarks). Then the room-wide Grand Magicks' lines.
 */
export function drawTimeStop(ctx: Ctx, cam: Camera, vm: ViewModel, w: number, h: number, zoom: number, time: number): void {
  const ts = vm.wave?.ts ?? 0;
  if (!(ts > 0)) { drawGrandLines(ctx, vm, w, h, 96); return; }
  if (vm.wave?.tsl === 1) { drawLesserStop(ctx, vm, w, h, ts); drawGrandLines(ctx, vm, w, h, 124); return; }
  const strength = Math.min(1, ts / 0.5);
  ctx.save();
  ctx.globalCompositeOperation = 'saturation';
  ctx.globalAlpha = 0.85 * strength;
  ctx.fillStyle = '#808080';
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 0.18 * strength;
  ctx.fillStyle = '#4A6A9A';
  ctx.fillRect(0, 0, w, h);
  ctx.globalAlpha = 1;
  // the caster's clock ring
  const by = vm.players.find((p) => p.id === vm.wave?.tsb);
  if (by) {
    const x = cam.sx(by.rx) * zoom, y = cam.sy(by.ry) * zoom;
    const r = 1.4 * UNIT * zoom;
    ctx.strokeStyle = rgba(UI.gold, 0.9);
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * 0.5, 0, 0, TAU);
    ctx.stroke();
    const hand = -time * 2;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(hand) * r * 0.8, y + Math.sin(hand) * r * 0.4);
    ctx.stroke();
  }
  ctx.font = font(22, 900);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillStyle = rgba(UI.gold, 0.95);
  ctx.fillText(`TIME STOPPED  ${ts.toFixed(1)}`, w / 2, 96);
  ctx.restore();
  drawGrandLines(ctx, vm, w, h, 124);
}

/** The Lesser Time Stop: frosted edges in the Moment's gold, and the line at the top. */
function drawLesserStop(ctx: Ctx, vm: ViewModel, w: number, h: number, ts: number): void {
  const strength = Math.min(1, ts / 0.4);
  ctx.save();
  const g = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.3, w / 2, h / 2, Math.max(w, h) * 0.62);
  g.addColorStop(0, rgba('#4A6A9A', 0));
  g.addColorStop(1, rgba('#7A9AC8', 0.6 * strength));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  // a thin gold frame: the Moment's colour, the caster's side runs free
  ctx.strokeStyle = rgba(elementColor('moment'), 0.55 * strength);
  ctx.lineWidth = 4;
  ctx.strokeRect(2, 2, w - 4, h - 4);
  ctx.font = font(20, 900);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillStyle = rgba(elementColor('moment'), 0.95);
  const by = vm.players.find((p) => p.id === vm.wave?.tsb);
  ctx.fillText(`ENEMIES STOPPED  ${ts.toFixed(1)}${by ? `  (${by.name})` : ''}`, w / 2, 96);
  ctx.restore();
}
